import {
  Cable as CableIcon,
  Tune as TuneIcon,
  Category as CategoryIcon,
  VerifiedUser as VerifiedUserIcon,
  ViewInAr as ViewInArIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material';
import type { SvgIconComponent } from '@mui/icons-material';
import type { SeverityLevel, ValidationCategory, ValidationDefinition } from './types';
import {
  EXPORT_READINESS_SUMMARY_VALIDATION_ID,
  normalizeValidationId,
} from '../../shared/config/validationCatalog';
export {
  EXPORT_READINESS_SUMMARY_VALIDATION_ID,
  normalizeValidationId,
} from '../../shared/config/validationCatalog';

export interface CategoryMeta {
  label: string;
  icon: SvgIconComponent;
  description: string;
  order: number;
  color: string; // palette key like 'info.main', 'warning.main', etc.
}

export const CATEGORY_META: Record<ValidationCategory, CategoryMeta> = {
  Connectivity: {
    label: 'Connectivity',
    icon: CableIcon,
    description: 'Connector alignment, END-TYPE, pipe sizing, system breaks',
    order: 1,
    color: 'info.main',
  },
  Parameters: {
    label: 'Parameters',
    icon: TuneIcon,
    description: 'Required parameters, values, formats, and naming',
    order: 2,
    color: 'primary.main',
  },
  Component: {
    label: 'Component',
    icon: CategoryIcon,
    description: 'Object types, categories, and component classification',
    order: 3,
    color: 'secondary.main',
  },
  'Data Quality': {
    label: 'Data quality',
    icon: VerifiedUserIcon,
    description: 'Data consistency, duplicates, and completeness',
    order: 4,
    color: 'warning.main',
  },
  Geometry: {
    label: 'Geometry',
    icon: ViewInArIcon,
    description: 'Coordinates, dimensions, and spatial validation',
    order: 5,
    color: 'success.main',
  },
  Summary: {
    label: 'Summary',
    icon: AssessmentIcon,
    description: 'Overall report and aggregated results',
    order: 6,
    color: 'info.main',
  },
};

export const SEVERITY_CONFIG = {
  Critical: { paletteColor: 'error' as const, label: 'Critical' },
  Error: { paletteColor: 'error' as const, label: 'Error' },
  Warning: { paletteColor: 'warning' as const, label: 'Warning' },
  Info: { paletteColor: 'info' as const, label: 'Info' },
} as const;

export const STATUS_CONFIG = {
  Passed: { paletteColor: 'success' as const, label: 'Passed' },
  Failed: { paletteColor: 'error' as const, label: 'Failed' },
  Warning: { paletteColor: 'warning' as const, label: 'Warning' },
  Running: { paletteColor: 'info' as const, label: 'Running' },
  NotRun: { paletteColor: 'default' as const, label: 'Not run' },
} as const;

/** The ValidationId used for connectivity checks (reconnect logic) */
export const CONNECTIVITY_VALIDATION_ID = 1;
/** ValidationIds that support reconnect actions */
export const RECONNECT_VALIDATION_IDS = [1, 21, 22, 28] as const;
const REMOVED_VALIDATION_IDS = [29, 27, 12] as const;
const REMOVED_VALIDATION_FIELD_TOKENS = ['SUPPORTTYPE'] as const;

export function isReconnectValidationId(validationId: number): boolean {
  return (RECONNECT_VALIDATION_IDS as readonly number[]).includes(normalizeValidationId(validationId));
}

export function isRemovedValidationId(validationId: number): boolean {
  return (REMOVED_VALIDATION_IDS as readonly number[]).includes(normalizeValidationId(validationId));
}

function normalizeValidationFieldToken(value: string | null | undefined): string {
  return (value || '').trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
}

export function isRemovedValidationFieldName(value: string | null | undefined): boolean {
  const normalizedToken = normalizeValidationFieldToken(value);
  return normalizedToken.length > 0 &&
    (REMOVED_VALIDATION_FIELD_TOKENS as readonly string[]).some((token) => normalizedToken.includes(token));
}

export const VALIDATION_CRITICALITY_RANKS: Readonly<Record<number, number>> = {
  2: 1,
  1: 2,
  13: 3,
  28: 4,
  11: 5,
  3: 6,
  4: 7,
  33: 8,
  5: 9,
  7: 10,
  15: 11,
  8: 12,
  35: 13,
  34: 14,
  26: 15,
  22: 16,
  20: 17,
  6: 18,
  21: 19,
  30: 20,
};

export const VALIDATION_DEFAULT_SEVERITIES: Readonly<Record<number, SeverityLevel>> = {
  2: 'Critical',
  1: 'Critical',
  13: 'Critical',
  28: 'Critical',
  11: 'Critical',
  3: 'Error',
  4: 'Error',
  33: 'Error',
  5: 'Error',
  7: 'Error',
  15: 'Error',
  8: 'Error',
  35: 'Warning',
  34: 'Warning',
  26: 'Warning',
  22: 'Warning',
  20: 'Warning',
  6: 'Info',
  21: 'Info',
  30: 'Info',
};

export const VALIDATION_CANONICAL_TITLES: Readonly<Record<number, string>> = {
  8: 'System assignment error check',
  11: 'Mating connector end-type compatibility',
};

const ORDERED_VALIDATION_IDS = Object.keys(VALIDATION_CRITICALITY_RANKS)
  .map((validationId) => Number(validationId))
  .sort((leftValidationId, rightValidationId) => {
    const leftRank = VALIDATION_CRITICALITY_RANKS[leftValidationId];
    const rightRank = VALIDATION_CRITICALITY_RANKS[rightValidationId];
    if (leftRank !== rightRank) {
      return leftRank - rightRank;
    }

    return normalizeValidationId(leftValidationId) - normalizeValidationId(rightValidationId);
  });

const VALIDATION_DISPLAY_NUMBER_BY_ID: Readonly<Record<number, number>> = ORDERED_VALIDATION_IDS.reduce(
  (acc, validationId, index) => {
    acc[validationId] = index + 1;
    return acc;
  },
  {} as Record<number, number>
);

export interface ValidationPriorityMeta {
  rank: number;
  urgencyLabel: string;
  urgencyDescription: string;
  tone: 'critical' | 'error' | 'warning' | 'info';
  severityLabel: 'Critical error' | 'Error' | 'Warning' | 'Info';
}

export function isSelectableValidationId(validationId: number): boolean {
  const normalizedValidationId = normalizeValidationId(validationId);
  return normalizedValidationId !== EXPORT_READINESS_SUMMARY_VALIDATION_ID
    && !isRemovedValidationId(normalizedValidationId);
}

export function resolveValidationIdCriticalityRank(validationId: number): number {
  return VALIDATION_CRITICALITY_RANKS[normalizeValidationId(validationId)] ?? Number.MAX_SAFE_INTEGER;
}

export function resolveValidationDefaultSeverity(validationId: number): SeverityLevel | null {
  return VALIDATION_DEFAULT_SEVERITIES[normalizeValidationId(validationId)] ?? null;
}

export function resolveValidationDisplayNumber(validationIds: number[] | number): number | null {
  const ids = Array.isArray(validationIds) ? validationIds : [validationIds];
  if (ids.length === 0) {
    return null;
  }

  const normalizedIds = ids.map(normalizeValidationId);
  const primaryValidationId = normalizedIds.reduce((bestId, candidateId) => {
    const bestRank = resolveValidationIdCriticalityRank(bestId);
    const candidateRank = resolveValidationIdCriticalityRank(candidateId);
    if (candidateRank !== bestRank) {
      return candidateRank < bestRank ? candidateId : bestId;
    }

    return candidateId < bestId ? candidateId : bestId;
  });

  return VALIDATION_DISPLAY_NUMBER_BY_ID[primaryValidationId] ?? null;
}

export function resolveVisibleValidationNumber(validationIds: number[] | number): number {
  const displayNumber = resolveValidationDisplayNumber(validationIds);
  if (displayNumber != null) {
    return displayNumber;
  }

  const ids = Array.isArray(validationIds) ? validationIds : [validationIds];
  const finiteIds = ids.filter((validationId) => Number.isFinite(validationId) && validationId > 0);
  return finiteIds.length > 0 ? Math.min(...finiteIds) : 0;
}

export function resolveCanonicalValidationTitle(
  validationId: number,
  fallbackTitle?: string | null
): string {
  const normalizedValidationId = normalizeValidationId(validationId);
  return VALIDATION_CANONICAL_TITLES[normalizedValidationId] ?? (fallbackTitle || '');
}

export function canValidationDisplayNumberBlockExport(validationIds: number[] | number): boolean {
  const displayNumber = resolveValidationDisplayNumber(validationIds);
  return displayNumber != null && displayNumber <= 5;
}

export function resolveValidationPriorityMeta(validationIds: number[] | number): ValidationPriorityMeta {
  const ids = Array.isArray(validationIds) ? validationIds : [validationIds];
  const rank = ids.reduce((lowestRank, validationId) => {
    return Math.min(lowestRank, resolveValidationIdCriticalityRank(validationId));
  }, Number.MAX_SAFE_INTEGER);

  if (rank <= 5) {
    return {
      rank,
      urgencyLabel: 'Fix now',
      urgencyDescription: 'Shows the highest-priority validation issues to address first. Lower priority numbers indicate higher urgency and should be handled before issues with higher numbers.',
      tone: 'critical',
      severityLabel: 'Critical error',
    };
  }

  if (rank <= 12) {
    return {
      rank,
      urgencyLabel: 'High priority',
      urgencyDescription: 'High-impact validation issues that should be addressed early in the repair workflow. Lower priority numbers indicate higher urgency and should be handled before issues with higher numbers.',
      tone: 'error',
      severityLabel: 'Error',
    };
  }

  if (rank <= 20) {
    return {
      rank,
      urgencyLabel: 'Address soon',
      urgencyDescription: 'Important validation issues that should be addressed after higher-priority issues are under control. Lower priority numbers indicate higher urgency and should be handled before issues with higher numbers.',
      tone: 'warning',
      severityLabel: 'Warning',
    };
  }

  return {
    rank,
    urgencyLabel: 'Can wait',
    urgencyDescription: 'Lower-urgency validation issues that can be addressed after higher-priority work is complete. Higher priority numbers indicate lower urgency and should be addressed after issues with lower numbers.',
    tone: 'info',
    severityLabel: 'Info',
  };
}

export function resolveValidationCriticalityRank(
  validation: Pick<ValidationDefinition, 'Id' | 'CriticalityRank'>
): number {
  const rank = Number(validation.CriticalityRank);
  if (Number.isFinite(rank) && rank > 0) {
    return rank;
  }

  return resolveValidationIdCriticalityRank(validation.Id);
}

export function compareValidationIdsByCriticality(leftValidationId: number, rightValidationId: number): number {
  const rankDifference =
    resolveValidationIdCriticalityRank(leftValidationId) -
    resolveValidationIdCriticalityRank(rightValidationId);

  if (rankDifference !== 0) {
    return rankDifference;
  }

  return normalizeValidationId(leftValidationId) - normalizeValidationId(rightValidationId);
}

export function compareValidationDefinitionsByCriticality(
  left: Pick<ValidationDefinition, 'Id' | 'CriticalityRank'>,
  right: Pick<ValidationDefinition, 'Id' | 'CriticalityRank'>
): number {
  const rankDifference =
    resolveValidationCriticalityRank(left) - resolveValidationCriticalityRank(right);

  if (rankDifference !== 0) {
    return rankDifference;
  }

  return normalizeValidationId(left.Id) - normalizeValidationId(right.Id);
}

// ─── Check Catalog: code + category for each validation check ───────────

export interface CheckCatalogEntry {
  code: string;
  category: ValidationCategory;
  autoFixable: boolean;
}

export const CHECK_CATALOG: Record<number, CheckCatalogEntry> = {
  1:  { code: 'CONN-001', category: 'Connectivity',  autoFixable: false },
  2:  { code: 'DATA-002', category: 'Data Quality',   autoFixable: true  },
  3:  { code: 'DATA-003', category: 'Data Quality',   autoFixable: false },
  4:  { code: 'DATA-004', category: 'Data Quality',   autoFixable: true  },
  5:  { code: 'DATA-005', category: 'Data Quality',   autoFixable: false },
  6:  { code: 'GEOM-006', category: 'Geometry',       autoFixable: false },
  7:  { code: 'COMP-007', category: 'Component',      autoFixable: true  },
  8:  { code: 'COMP-008', category: 'Component',      autoFixable: true  },
  11: { code: 'CONN-011', category: 'Connectivity',   autoFixable: true  },
  13: { code: 'PARAM-013', category: 'Parameters',    autoFixable: false },
  15: { code: 'PARAM-015', category: 'Parameters',    autoFixable: false },
  20: { code: 'COMP-020', category: 'Component',      autoFixable: false },
  21: { code: 'CONN-021', category: 'Connectivity',   autoFixable: false },
  22: { code: 'CONN-022', category: 'Connectivity',   autoFixable: false },
  26: { code: 'PARAM-026', category: 'Parameters',    autoFixable: false },
  28: { code: 'GEOM-028', category: 'Geometry',       autoFixable: false },
  30: { code: 'SUM-030',  category: 'Summary',        autoFixable: false },
  33: { code: 'PARAM-033', category: 'Parameters',    autoFixable: true  },
  34: { code: 'COMP-034', category: 'Component',      autoFixable: false },
  35: { code: 'PARAM-035', category: 'Parameters',    autoFixable: false },
};

export function getCheckCatalogEntry(validationId: number): CheckCatalogEntry | null {
  return CHECK_CATALOG[normalizeValidationId(validationId)] ?? null;
}

/** All known validation categories in display order */
export const CATEGORY_ORDER: ValidationCategory[] = [
  'Connectivity',
  'Parameters',
  'Component',
  'Data Quality',
  'Geometry',
  'Summary',
];

/**
 * Map a free-form category string to a known ValidationCategory.
 * Falls back to 'Parameters' if unrecognized.
 */
export function resolveCategory(raw: string): ValidationCategory {
  const normalized = raw.trim();
  if (normalized in CATEGORY_META) return normalized as ValidationCategory;

  // Common aliases
  const lower = normalized.toLowerCase();
  if (lower.includes('connect')) return 'Connectivity';
  if (lower.includes('param')) return 'Parameters';
  if (lower.includes('component') || lower.includes('object')) return 'Component';
  if (lower.includes('data') || lower.includes('quality')) return 'Data Quality';
  if (lower.includes('geom') || lower.includes('coord')) return 'Geometry';
  if (lower.includes('summ') || lower.includes('report')) return 'Summary';

  return 'Parameters';
}
